import { useState, useEffect, useMemo } from "react";
import BarraSuperior from "../barra_superior";
import AnimacionCarga from "../animacion_de_carga";
import { consultarSimple } from "../../../functions/crud";
import { CONFIG } from "../../../functions/variables_globales";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { crearTitulo, crearGrafica, subirDatos } from "../../../functions/gestion";

const DIAS_SEMANA = ['Viernes', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Viernes'];
const DIAS_LABORALES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

const ajustarFechaFinSemana = (fechaStr) => {
  const fecha = new Date(fechaStr + 'T00:00:00');
  const dia = fecha.getDay();
  if (dia === 6) fecha.setDate(fecha.getDate() + 2);
  else if (dia === 0) fecha.setDate(fecha.getDate() + 1);
  return fecha.toISOString().split('T')[0];
};

const EstadisticasActividades = () => {
  const [cargando, setCargando] = useState(false);
  const [actividades, setActividades] = useState([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [filtros, setFiltros] = useState({
    fechaInicio: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    fechaFin: new Date().toISOString().split('T')[0],
    usuario: 'todos'
  });

  useEffect(() => {
    Chart.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const listado = await consultarSimple(`actividades/${CONFIG.codigoCliente()}/listado`);
      const datos = Object.entries(listado || {}).map(([id, item]) => ({ 
        id, 
        ...item,
        fecha_inicio: ajustarFechaFinSemana(item.fecha_inicio)
      }));
      
      setActividades(datos);
      setUsuariosDisponibles([...new Set(datos.map(d => d.responsable).filter(Boolean))].sort());
    } catch (error) {
      console.error("Error al cargar datos:", error);
    } finally {
      setCargando(false);
    }
  };

  const actividadesFiltradas = useMemo(() => 
    actividades.filter(act => 
      act.fecha_inicio >= filtros.fechaInicio && 
      act.fecha_inicio <= filtros.fechaFin && 
      (filtros.usuario === 'todos' || act.responsable === filtros.usuario)
    ), [actividades, filtros]
  );

  const estadisticas = useMemo(() => {
    const stats = {
      horasPorDia: {}, actividadesPorDia: {},
      horasPorDiaSemana: Object.fromEntries(DIAS_SEMANA.map(d => [d, 0])),
      actividadesPorDiaSemana: Object.fromEntries(DIAS_SEMANA.map(d => [d, 0])),
      horasPorMes: {}, actividadesPorMes: {},
      horasPorResponsable: {}, actividadesPorResponsable: {},
      actividadesPorEstado: {}, horasPorEstado: {},
      actividadesPorTipo: {}, horasPorTipo: {},
      totalActividades: 0, totalHoras: 0, totalHorasLaborales: 0,
    };

    actividadesFiltradas.forEach(act => {
      const horas = parseFloat(act.tiempo_real) || 0;
      const fecha = act.fecha_inicio;
      const diaSemana = DIAS_SEMANA[new Date(fecha).getDay()];
      const mes = fecha.substring(0, 7);
      const dia = fecha.substring(0, 10);

      stats.totalActividades++;
      stats.totalHoras += horas;
      if (DIAS_LABORALES.includes(diaSemana)) stats.totalHorasLaborales += horas;

      stats.horasPorDia[dia] = (stats.horasPorDia[dia] || 0) + horas;
      stats.actividadesPorDia[dia] = (stats.actividadesPorDia[dia] || 0) + 1;
      stats.horasPorDiaSemana[diaSemana] += horas;
      stats.actividadesPorDiaSemana[diaSemana]++;
      stats.horasPorMes[mes] = (stats.horasPorMes[mes] || 0) + horas;
      stats.actividadesPorMes[mes] = (stats.actividadesPorMes[mes] || 0) + 1;

      if (act.responsable) {
        stats.horasPorResponsable[act.responsable] = (stats.horasPorResponsable[act.responsable] || 0) + horas;
        stats.actividadesPorResponsable[act.responsable] = (stats.actividadesPorResponsable[act.responsable] || 0) + 1;
      }
      if (act.estado) {
        stats.actividadesPorEstado[act.estado] = (stats.actividadesPorEstado[act.estado] || 0) + 1;
        stats.horasPorEstado[act.estado] = (stats.horasPorEstado[act.estado] || 0) + horas;
      }
      if (act.tipo) {
        stats.actividadesPorTipo[act.tipo] = (stats.actividadesPorTipo[act.tipo] || 0) + 1;
        stats.horasPorTipo[act.tipo] = (stats.horasPorTipo[act.tipo] || 0) + horas;
      }
    });

    return stats;
  }, [actividadesFiltradas]);

  const metricas = useMemo(() => {
    const { totalActividades, totalHoras, horasPorDia, horasPorMes } = estadisticas;
    const diasConActividad = Object.keys(horasPorDia).length;
    const mesesConActividad = Object.keys(horasPorMes).length;
    
    const diaMasHoras = Object.entries(horasPorDia).reduce((max, [fecha, horas]) => 
      horas > max.horas ? { fecha, horas } : max, { fecha: '-', horas: 0 }
    );

    const diaMenosHoras = Object.entries(horasPorDia).reduce((min, [fecha, horas]) => 
      horas > 0 && horas < min.horas ? { fecha, horas } : min, { fecha: '-', horas: Infinity }
    );

    const diasPeriodo = Math.ceil((new Date(filtros.fechaFin) - new Date(filtros.fechaInicio)) / (1000 * 60 * 60 * 24));

    return {
      promedioDiario: diasConActividad > 0 ? (totalHoras / diasConActividad).toFixed(2) : 0,
      promedioSemanal: diasPeriodo > 0 ? (totalHoras / (diasPeriodo / 7)).toFixed(2) : 0,
      promedioMensual: mesesConActividad > 0 ? (totalHoras / mesesConActividad).toFixed(2) : 0,
      promedioPorActividad: totalActividades > 0 ? (totalHoras / totalActividades).toFixed(2) : 0,
      diaMasHoras: `${diaMasHoras.fecha} (${diaMasHoras.horas.toFixed(2)} hrs)`,
      diaMenosHoras: diaMenosHoras.horas === Infinity ? '- (0 hrs)' : `${diaMenosHoras.fecha} (${diaMenosHoras.horas.toFixed(2)} hrs)`,
      tasaFinalizacion: totalActividades > 0 ? `${((estadisticas.actividadesPorEstado['Finalizado'] || 0) / totalActividades * 100).toFixed(1)}%` : '0%',
    };
  }, [estadisticas, filtros]);

  const resumenGeneral = useMemo(() => [
    { concepto: 'Usuario', valor: filtros.usuario === 'todos' ? 'Todos los usuarios' : filtros.usuario },
    { concepto: 'Período', valor: `${filtros.fechaInicio} a ${filtros.fechaFin}` },
    { concepto: 'Total Actividades', valor: estadisticas.totalActividades },
    { concepto: 'Total Horas', valor: estadisticas.totalHoras.toFixed(2) },
    { concepto: 'Horas Días Laborales', valor: estadisticas.totalHorasLaborales.toFixed(2) },
    { concepto: 'Promedio Horas/Día', valor: metricas.promedioDiario },
    { concepto: 'Promedio Horas/Semana', valor: metricas.promedioSemanal },
    { concepto: 'Promedio Horas/Mes', valor: metricas.promedioMensual },
    { concepto: 'Promedio Horas/Actividad', valor: metricas.promedioPorActividad },
    { concepto: 'Día con Más Horas', valor: metricas.diaMasHoras },
    { concepto: 'Día con Menos Horas', valor: metricas.diaMenosHoras },
    { concepto: 'Tasa Finalización', valor: metricas.tasaFinalizacion },
  ], [estadisticas, filtros, metricas]);

  if (cargando) return <AnimacionCarga />;

  return (
    <div>
      <BarraSuperior />
      <div className="BloqueModulo">
        <br /><br /><br />
        <div style={{ padding: '0 10px 20px 10px' }}>
          <h2>Estadísticas de Actividades</h2>
          <br />

          {}
          <div style={{ display: 'flex', gap: '30px', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>Desde:</label>
              <input 
                type="date" 
                value={filtros.fechaInicio} 
                onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value })} 
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>Hasta:</label>
              <input 
                type="date" 
                value={filtros.fechaFin} 
                onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })} 
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>Usuario:</label>
              <select 
                value={filtros.usuario} 
                onChange={(e) => setFiltros({ ...filtros, usuario: e.target.value })}
              >
                <option value="todos">Todos</option>
                {usuariosDisponibles.map(usuario => <option key={usuario} value={usuario}>{usuario}</option>)}
              </select>
            </div>
          </div>
        </div>

        {crearTitulo('Resumen General')}
        <br />
        <table id="table" className="tabla-responsive">
          <thead><tr><th>Concepto</th><th>Valor</th></tr></thead>
          <tbody>
            {resumenGeneral.map((item, index) => (
              <tr key={index}><td><strong>{item.concepto}</strong></td><td>{item.valor}</td></tr>
            ))}
          </tbody>
        </table>

        <br />
        <div className="botonIzquierda">
          <input type="button" className="cierreSesion" value="EXPORTAR DATOS" onClick={() => subirDatos(resumenGeneral)} />
        </div>
        <br />

        {crearTitulo('Eficiencia Semanal')}
        {crearGrafica('barra', 'Horas por día de la semana', estadisticas.horasPorDiaSemana, 600, 1200, "texto", true, false)}

        {crearTitulo('Análisis Temporal')}
        {crearGrafica('barra', 'Horas por mes', estadisticas.horasPorMes, 600, 1200, "texto", true, false)}
        {crearGrafica('barra', 'Horas por día', estadisticas.horasPorDia, 600, 1200, "texto", true, false)}

        {filtros.usuario === 'todos' && (
          <>
            {crearTitulo('Productividad por Responsable')}
            {crearGrafica('barra', 'Horas por responsable', estadisticas.horasPorResponsable, 600, 1200, "texto", true, false)}
            {crearGrafica('barra', 'Actividades por responsable', estadisticas.actividadesPorResponsable, 600, 1200, "texto", true, false)}
          </>
        )}

        {crearTitulo('Distribución por Estado')}
        {crearGrafica('barra', 'Horas por estado', estadisticas.horasPorEstado, 600, 1200, "texto", true, false)}

        {crearTitulo('Distribución por Tipo')}
        {crearGrafica('barra', 'Horas por tipo', estadisticas.horasPorTipo, 600, 1200, "texto", true, false)}
      </div>
    </div>
  );
};

export default EstadisticasActividades;